import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, InputNumber, Modal, Row, Tabs, Tooltip } from "antd";
import { OrderItem, useOrderList } from "../../../context/OrderListContext";
import BaseTable from "../../../components/BaseTable";
import { useItemsContext } from "../../../context/AppItemsContext";
import { ItemModel } from "../../../../infrastructure/model/ProductItemModel";
import {
  ITEM_TYPE_ENUM,
  LIMIT_PER_PAGE,
  ORDER_STATUS_ENUM,
  SALE_TYPE_ENUM,
  SALE_VALUE_TYPE_ENUM,
  SUGAR_ICE_OPTION,
} from "../../../../applications/constants/AppConst";
import { SectionModel } from "../../../../infrastructure/model/SectionModel";
import { useToppingContext } from "../../../context/AppToppingContext";
import usePageState from "../../../hooks/usePageState";
import { SaleInfoResponse } from "../../../../infrastructure/model/SaleInfoModel";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { CreateOrderRequest, OrderResponse } from "../../../../infrastructure/model/OrderModel";
import useForm from "../../../hooks/useForm";
import AppInputDropdownMultiple from "../../../components/AppDropdownMultiple";
import { currencyFormat } from "../../../../applications/utils/NumberUtils";
import "../styles/InStoreOrder.scss";
import AutoSizer from "react-virtualized-auto-sizer";
import useConfirmAction from "../../../hooks/useConfirmAction";
import OrderBillDialog from "./OrderBillDialog";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import Visible from "../../../components/Visible";
import { AppDropdown } from "../../../components/AppDropdown";
import AppInputText from "../../../components/AppInputText/AppInputText";
import Search from "antd/es/input/Search";
import {
  getSizeMappingByItem,
  getSizeMappingOptionByItem,
  handleCustomChange,
  handleSugarIceChange,
  splitNote,
} from "../../../../applications/useCase/BusinessUseCase/ConvertNoteUseCase";
import {
  createOrderUseCase,
  getItemInOrderUseCase,
  updateOrderUseCase,
} from "../../../../applications/useCase/ApiUseCase/OrderApiUseCase";
import { getAllSaleInfoUseCase } from "../../../../applications/useCase/ApiUseCase/SaleApiUseCase";
import { getSaleScreenSectionMenu } from "../../../../applications/useCase/BusinessUseCase/SectionDataUseCase";
import { calculateOrderPrice, OrderCalculationResult } from "../../../../applications/useCase/BusinessUseCase/CalculateOrderPriceUseCase";

interface InStoreOrderProps {
  redirectToPending: () => void;
  updateOrderId?: number;
}

function InStoreOrder(props: InStoreOrderProps) {
  const { redirectToPending, updateOrderId } = props;
  const [openOrderBill, setOpenOrderBill] = useState<boolean>(false);
  const [customNoteMap, setCustomNoteMap] = useState<Map<number, string>>(new Map());
  const [selectedItem, setSelectedItem] = useState<OrderResponse>();
  const { orderList, updateOrder, removeFromOrder, addToOrder, clearOrder } = useOrderList();
  const { showError, setLoading, showClientError, showSuccess } = usePageState();
  const { items } = useItemsContext();
  const { toppings } = useToppingContext();
  const [saleActive, setSaleActive] = useState<SaleInfoResponse[]>([]);
  const [activeTab, setActiveTab] = useState<string>("0");
  const { TabPane } = Tabs;

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const activeItems: ItemModel[] = useMemo(() => {
    const list = items?.filter((t) => t.open_sale) || [];
    if (!debouncedSearch) return list;
    return list.filter((item) => item.name.toLowerCase().includes(debouncedSearch));
  }, [items, debouncedSearch]);

  const toppingCanAddMore = useMemo(() => {
    return toppings?.filter((e) => e.can_sale);
  }, [toppings]);

  const { dialog: confirmSaveDialog, confirm: handleConfirmSave } = useConfirmAction({
    onConfirm: () => formik.checkErrorAndSubmit(showClientError),
    message: "Bạn có chắc chắn muốn lưu đơn hàng này không?",
    header: "Lưu đơn hàng",
  });

  const { dialog: confirmPrintDialog, confirm: handleConfirmSaveAndPrint } = useConfirmAction({
    onConfirm: () =>
      saveAndPrintOrder({ ...formik.values, order_status: ORDER_STATUS_ENUM.APPROVE }),
    message: "Bạn có chắc chắn muốn lưu và in đơn hàng này không?",
    header: "In đơn hàng",
  });

  async function saveAndPrintOrder(request: CreateOrderRequest) {
    const res = await createOrderUseCase({ request, setLoading, showError, showSuccess });
    if (res) {
      setOpenOrderBill(true);
      setSelectedItem(res);
    }
  }

  async function getAllSaleInfo() {
    const res = await getAllSaleInfoUseCase({
      page: 1,
      limit: LIMIT_PER_PAGE,
      is_sale_order: undefined,
      is_active: true,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setSaleActive(res.data);
    }
  }

  useAsyncEffect(async () => {
    await getAllSaleInfo();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  const orderPrice: OrderCalculationResult = useMemo(() => {
    if (!orderList) return {} as OrderCalculationResult;
    return calculateOrderPrice(orderList, saleActive);
  }, [saleActive, orderList]);

  // Lọc khuyến mãi không trùng lặp theo id
  const bestSale = useMemo(() => {
    return orderPrice?.bestSale?.sale ? [orderPrice?.bestSale.sale] : [];
  }, [orderPrice.bestSale]);

  async function createOrder(request: CreateOrderRequest) {
    let res: any;
    if (updateOrderId) {
      res = await updateOrderUseCase({
        orderId: updateOrderId,
        request,
        setLoading,
      });
    } else {
      res = await createOrderUseCase({
        request,
        showError,
        setLoading,
      });
    }
    if (res) {
      Modal.success({
        title: "Thành công",
        content: updateOrderId ? "Sửa đơn hàng thành công" : "Đơn hàng đã được đặt thành công.",
        onOk: () => {
          clearOrder();
          redirectToPending();
        },
      });
    }
  }

  const formik = useForm<CreateOrderRequest>({
    initialValues: {
      username: "",
      user_phone: "",
      address: "",
      order_note: "",
      voucher_ids: bestSale?.map((e) => e.id) || [],
      original_price: orderPrice?.originalTotal,
      discount_price: orderPrice?.discountedTotal || orderPrice?.originalTotal,
      items: [],
      take_away: false,
    },
    onSubmit: async (values) => {
      await createOrder(values);
    },
  });

  useEffect(() => {
    if (!orderPrice) return;

    formik.setValues((prev) => ({
      ...prev,
      original_price: orderPrice.originalTotal,
      discount_price: orderPrice.discountedTotal || orderPrice.originalTotal,
      voucher_ids: bestSale?.map((e) => e.id) || [],
      items:
        orderPrice?.items?.flatMap((e) => {
          const baseItem = {
            item_id: e.id,
            item_quantity: e.quantity,
            item_note: e.note,
            item_size: e.size,
            item_price: e.price,
            topping_ids: e.moreTopping?.map((t) => t.id),
          };
          const result = [baseItem];
          return result;
        }) ?? [],
    }));
  }, [orderPrice, bestSale]);

  const handleAddItemToOrder = (item: ItemModel) => {
    let quantity = 1;
    const otherItem = orderList.find((s) => s.id === item.id);
    if ([ITEM_TYPE_ENUM.OTHER, ITEM_TYPE_ENUM.TOPPING]?.includes(item.type) && otherItem?.id) {
      quantity = (otherItem.quantity || 0) + 1;
      updateOrder({ ...otherItem, quantity });
    } else {
      addToOrder({
        id: item.id,
        name: item.name,
        size: item?.size_mapping?.[0]?.size,
        price: item?.size_mapping?.[0]?.price,
        quantity: quantity,
        image: item.image,
        type: item.type,
      });
    }
  };

  const sections: SectionModel[] = useMemo(() => {
    return getSaleScreenSectionMenu(activeItems);
  }, [activeItems]);

  async function getItemsInOrder(orderId: number) {
    const res = await getItemInOrderUseCase({
      orderId,
      setLoading,
      showError,
    });
    if (res) {
      const newMap = new Map(customNoteMap);
      res?.forEach((itemMapping, index) => {
        const currentItem = activeItems?.find((t) => t.id === itemMapping.item_id);
        const currentPrice = currentItem?.size_mapping?.find(
          (s) => s.size === itemMapping.item_size
        )?.price;
        addToOrder({
          id: itemMapping.item_id,
          name: currentItem?.name!,
          price: currentPrice!,
          quantity: itemMapping.item_quantity,
          size: itemMapping.item_size,
          indexAdd: index,
          type: currentItem?.type!,
          note: itemMapping.item_note,
          moreTopping: toppings?.filter((t) => itemMapping.topping_ids?.includes(t.id)),
        });
        const { sugarIce, custom } = splitNote(itemMapping?.item_note || "");
        newMap.set(index, custom); // hoặc orderItem.id nếu có
      });
      setCustomNoteMap(newMap);
    }
  }

  useAsyncEffect(async () => {
    if (!updateOrderId) return;
    clearOrder();
    await getItemsInOrder(updateOrderId);
  }, [updateOrderId]);

  useEffect(() => {
    if (!debouncedSearch) return;

    // Tìm item đầu tiên match
    const foundItem = activeItems.find((item) => item.name.toLowerCase().includes(debouncedSearch));

    if (!foundItem) return;

    // Tìm tab chứa item
    const section = sections.find((s) => s?.data?.some((i) => i.id === foundItem.id));

    if (section) {
      const targetTab = section.id.toString();

      // Chỉ chuyển tab nếu khác tab hiện tại
      if (activeTab !== targetTab) {
        setActiveTab(targetTab);
      }

      // Scroll sau khi tab đã render (chờ 200ms để chắc chắn)
      setTimeout(() => {
        const el = document.getElementById(`drink-${foundItem.id}`);
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 200);
    }
  }, [debouncedSearch, activeItems, sections, activeTab]);

  const columns = [
    {
      title: "Tên món",
      dataIndex: "name",
      render: (_: any, order: OrderItem) => {
        const noteOptions = [ITEM_TYPE_ENUM.COFFEE_MATCHA_HOT_DRINK]?.includes(order.type)
          ? SUGAR_ICE_OPTION
          : SUGAR_ICE_OPTION?.filter((_, i) => i !== 0);
        const { sugarIce, custom } = splitNote(order?.note || "");
        return (
          <>
            <div style={{ fontWeight: 500 }}>{order.name}</div>
            {/* Dropdown thêm topping */}
            <Visible
              isVisible={[
                ITEM_TYPE_ENUM.BLACK_MILK_TEA,
                ITEM_TYPE_ENUM.OOLONG_MILK_TEA,
                ITEM_TYPE_ENUM.FRESH_FRUIT_JUICE,
                ITEM_TYPE_ENUM.NON_CAFFEIN,
              ].includes(order.type)}
            >
              <AppInputDropdownMultiple
                options={toppingCanAddMore?.map((e) => ({
                  label: `${e.name} + ${currencyFormat(e.price)}`,
                  value: e.id,
                }))}
                className="w-100 p-1"
                placeholder="Thêm topping"
                currentValue={order.moreTopping?.map((e) => e.id)}
                onChangeValue={(newToppingIds: number[]) => {
                  updateOrder({
                    ...order,
                    moreTopping: toppingCanAddMore?.filter((t) => newToppingIds.includes(t.id)),
                  });
                }}
              />
            </Visible>
            <Visible
              isVisible={![ITEM_TYPE_ENUM.OTHER, ITEM_TYPE_ENUM.TOPPING]?.includes(order.type)}
            >
              <div className="p-1 ">
                <AppInputDropdownMultiple
                  currentValue={sugarIce}
                  maxCount={2}
                  onChangeValue={(e: string[]) => {
                    handleSugarIceChange(order, e, updateOrder);
                  }}
                  placeholder="Đường, đá..."
                  options={noteOptions}
                />
              </div>
              <div className="p-1 ">
                <AppInputText
                  currentValue={customNoteMap.get(order.indexAdd!) || ""}
                  onTextChange={(e: string) => {
                    handleCustomChange(order, e, updateOrder, customNoteMap, setCustomNoteMap);
                  }}
                  additionProps={{
                    placeholder: "Ghi chú khác",
                    maxLength: 30,
                  }}
                />
              </div>
            </Visible>
          </>
        );
      },
    },
    {
      title: "Size",
      width: 80,
      dataIndex: "size",
      render: (value: number, record: OrderItem) => (
        <AppDropdown
          options={getSizeMappingOptionByItem(activeItems, record.id)}
          currentValue={record.size}
          onChangeValue={(e) =>
            updateOrder({
              ...record,
              size: e,
              price:
                getSizeMappingByItem(activeItems, record.id)?.find((op) => op.size === e)?.price ||
                0,
            })
          }
        />
      ),
    },
    {
      title: "SL",
      width: 80,
      dataIndex: "quantity",
      render: (value: number, record: OrderItem) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(e) =>
            updateOrder({
              ...record,
              quantity: e!,
            })
          }
          style={{ width: "100%" }}
          size="small"
        />
      ),
    },
    {
      title: "Thành tiền",
      render: (_: any, record: OrderItem) => {
        const currentItemOrderPrice = orderPrice?.items?.find(
          (o) => o.indexAdd === record.indexAdd
        );
        const toppingTotal =
          record.moreTopping?.reduce(
            (sum, topping) => (topping.can_sale ? sum + topping.price : sum),
            0
          ) ?? 0;
        const basePrice = currentItemOrderPrice?.id
          ? (currentItemOrderPrice.price + toppingTotal) * currentItemOrderPrice.quantity
          : record.price * record.quantity;
        const finalPrice = currentItemOrderPrice?.id
          ? currentItemOrderPrice.finalPrice * currentItemOrderPrice.quantity
          : record.price * record.quantity;
        return (
          <>
            <div
              style={{
                color: "#888",
                textDecoration:
                  currentItemOrderPrice?.id &&
                  currentItemOrderPrice?.appliedSale &&
                  basePrice !== finalPrice
                    ? "line-through"
                    : "unset",
              }}
            >
              {currencyFormat(basePrice)}
            </div>
            <div
              style={{
                color: "#dd4f4f",
                fontWeight: 600,
                display:
                  currentItemOrderPrice?.id &&
                  currentItemOrderPrice?.appliedSale &&
                  basePrice !== finalPrice
                    ? "block"
                    : "none",
              }}
            >
              {currencyFormat(finalPrice)}
            </div>
          </>
        );
      },
    },
    {
      title: "",
      render: (_: any, record: OrderItem) => (
        <Button
          danger
          onClick={() => removeFromOrder(record?.indexAdd!)}
          icon={<DeleteOutlined />}
        ></Button>
      ),
    },
  ];

  return (
    <div className="h-100">
      <Row className="h-100">
        {/* Bên trái: Danh sách món đã chọn */}
        <Col
          span={10}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div className="w-100 flex-1" style={{ minHeight: 200 }}>
            <BaseTable
              dataSource={orderList}
              columns={columns}
              rowKey="indexAdd"
              pagination={false}
              size="small"
            />
          </div>

          <div style={{ textAlign: "right", minHeight: 150, height: "20%", position: "relative" }}>
            <div
              className="w-100 h-100"
              // style={{ overflow: "auto", maxHeight: "calc(100% - 80px)" }}
            >
              <Row className="flex flex-row w-100">
                <Col span={12} style={{ fontWeight: 600 }} className="flex pl-4">
                  Tổng tiền gốc:
                </Col>
                <Col span={12} className="flex justify-content-end pr-4">
                  {currencyFormat(orderPrice?.originalTotal)}
                </Col>
              </Row>

              {bestSale?.length > 0 &&
                orderPrice?.originalTotal !== orderPrice?.discountedTotal && (
                  <Row className="flex flex-row w-100 mt-1">
                    <Col span={12} style={{ fontWeight: 600 }} className="flex pl-4">
                      Sau khuyến mãi:
                    </Col>
                    <Col
                      span={12}
                      className="flex justify-content-end pr-4"
                      style={{ color: "#dd4f4f" }}
                    >
                      {currencyFormat(orderPrice?.discountedTotal)}
                    </Col>
                  </Row>
                )}

              {/* ✅ Hiển thị khuyến mãi đã áp dụng (cả đơn và sản phẩm) */}
              {bestSale?.length > 0 && (
                <div className="mt-2 " style={{ fontSize: 12, color: "#4096ff" }}>
                  <div className="pl-4">
                    <b>Khuyến mãi đã áp dụng:</b>
                  </div>
                  <ul className="mt-1">
                    {bestSale?.map((sale) => (
                      <li key={sale.id}>
                        🎁 <b>{sale.title}</b> ({sale.sale_value}
                        {sale.sale_value_type === SALE_VALUE_TYPE_ENUM.PERCENT
                          ? "%"
                          : sale.sale_value_type === SALE_VALUE_TYPE_ENUM.VND
                          ? "đ"
                          : "cốc"}{" "}
                        –
                        {sale.sale_type === SALE_TYPE_ENUM.ALL_BILL
                          ? "Toàn đơn"
                          : sale.sale_type === SALE_TYPE_ENUM.EACH_ITEM
                          ? "Sản phẩm"
                          : "Combo"}{" "}
                        )
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Row
              gutter={[8, 8]}
              style={{
                gap: "8px",
                justifyContent: "flex-end",
                position: "absolute",
                bottom: "5px",
                right: "5px",
              }}
            >
              <Button variant="solid" color="danger" type="primary" onClick={() => clearOrder()}>
                Hủy
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleConfirmSaveAndPrint()}
                disabled={orderList.length === 0}
              >
                In hoá đơn
              </Button>
              <Button
                type="primary"
                onClick={() => handleConfirmSave()}
                disabled={orderList.length === 0}
              >
                Lưu hoá đơn
              </Button>
            </Row>
          </div>
        </Col>

        {/* Bên phải: Danh sách sản phẩm theo section */}
        <Col span={14} className="p-1">
          <Row>
            <Search
              placeholder="Tìm kiếm theo tên đồ uống..."
              className="m-1"
              style={{ boxSizing: "border-box" }}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              allowClear
              enterButton={
                <Tooltip title="Nhập tên để tìm kiếm">
                  <SearchOutlined />
                </Tooltip>
              }
            />
          </Row>

          <Tabs
            activeKey={activeTab}
            onTabClick={(e) => {
              setSearchText("");
              setDebouncedSearch(""); // Clear luôn debounced để tránh conflict
              setActiveTab(String(e));
            }}
            type="card"
            tabPosition="top"
            style={{ height: `calc(100% - 40px)` }}
          >
            {sections.map((section) => (
              <TabPane tab={section.label} key={section.id.toString()} style={{ height: "100%" }}>
                <div style={{ height: "100%", width: "100%" }}>
                  <AutoSizer>
                    {({ height, width }) => (
                      <div style={{ height, width, overflowY: "auto", paddingRight: 8 }}>
                        <Row gutter={[8, 8]}>
                          {section?.data?.map((item) => (
                            <Col key={item.id} xs={24} sm={12} md={8} lg={6} xl={6}>
                              <Card
                                id={`drink-${item.id}`}
                                hoverable={item?.size_mapping?.length !== 0}
                                cover={
                                  <img
                                    alt={item.name}
                                    src={item.image}
                                    style={{ height: 120, objectFit: "cover" }}
                                  />
                                }
                                style={{ height: "300px" }}
                                onClick={() => {
                                  if (item.size_mapping?.length === 0) {
                                    showClientError("Món này không có giá, vui lòng liên hệ");
                                    return;
                                  }
                                  handleAddItemToOrder(item);
                                }}
                              >
                                <Card.Meta
                                  title={item.name}
                                  description={
                                    <div>
                                      <Visible isVisible={item?.size_mapping?.length === 0}>
                                        <p>Liên hệ</p>
                                      </Visible>
                                      <Visible isVisible={item.size_mapping?.length > 0}>
                                        <p style={{ fontWeight: "bold", color: "#DD4F4F" }}>
                                          {currencyFormat(item.size_mapping?.[0]?.price)}
                                        </p>
                                      </Visible>
                                      <p className="mt-1 item-topping">
                                        {toppings
                                          ?.filter((t) => item.attached?.includes(t.id))
                                          ?.map((t) => t.name)
                                          ?.join(", ")}
                                      </p>
                                    </div>
                                  }
                                />
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </AutoSizer>
                </div>
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
      {confirmSaveDialog}
      {confirmPrintDialog}
      {openOrderBill && selectedItem && (
        <OrderBillDialog
          onClose={() => {
            setOpenOrderBill(false);
            clearOrder();
          }}
          orderItem={selectedItem!}
          isTakeAway={false}
        />
      )}
    </div>
  );
}
export default InStoreOrder;
